// module.tanks-budget (contract §4.8.1): TanksBudgetModule (modules.tsx): one
// line per tank, written out to the module's twelve slots: its glyph (water,
// fuel or waste, by shape), its name with the short trailing token kept
// whole, its meter in the severity's colour (a dashed lane with no signal),
// and its level, with an alert mark past its line. "No tank levels
// reported." when the boat has none. The attention line is the module card's
// footer (§6.2).
//
// Each row's glyph field, which carries the tank's kind and severity, comes
// just before the row, so a slot the boat does not fill hides whole. The
// rows' density follows the tank count as today's (roomy to three tanks,
// normal to five, tight from six): two more copies of the fourth and sixth
// glyph fields, ahead of the list, say whether those tanks exist. The glyphs
// are drawn at their own 15 px and 13 px, so a row's svg needs no viewBox.
//
// The Marina look (TOM-678, as TOM-679's tank rows): each row's glyph in its
// fluid's symbol colour and its meter in its fluid's colour on a neutral
// track, fuel in fuel's and every water tank (fresh, grey, black and waste)
// in water's. A tank past its line keeps its colour; the warning is the
// alert mark and its value in the warning's tone.

import { css, h } from "./kit.js";
import { F, R } from "./module-kit.js";

const SLOTS = 12;

/** Each glyph's short id in the page's defs (short: twelve rows use them). */
const GLYPH_ID = { water: "w", fuel: "f", waste: "x" };

const GLYPHS = {
  water: h("path", {
    d: "M12 3C12 3 5 11 5 15a7 7 0 0 0 14 0C19 11 12 3 12 3Z",
  }),
  fuel: [
    h("rect", { x: "7", y: "7", width: "9", height: "12.5", rx: "1.6" }),
    h("rect", { x: "9.3", y: "4.2", width: "3.6", height: "3", rx: "0.8" }),
  ],
  waste: [
    h("path", {
      d: "M7 8.4h10l-1 10.2a1.8 1.8 0 0 1-1.8 1.6H9.8A1.8 1.8 0 0 1 8 18.6Z",
    }),
    h("rect", { x: "5.6", y: "6", width: "12.8", height: "2.1", rx: "1" }),
    h("rect", { x: "9.6", y: "3.5", width: "4.8", height: "2", rx: "0.9" }),
  ],
};

export function moduleTanksBudget() {
  const defs = h(
    "svg",
    { class: "tk-defs", "aria-hidden": "true" },
    h(
      "defs",
      {},
      Object.entries(GLYPHS).map(([kind, shape]) =>
        h("g", { id: `t${GLYPH_ID[kind]}`, transform: "scale(.625)" }, shape),
      ),
      h(
        "g",
        { id: "ta", transform: "scale(.5417)" },
        h("path", {
          d: "M12 3.6 22 20.4H2Z",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2.2",
          "stroke-linejoin": "round",
        }),
        h("rect", { x: "10.9", y: "9", width: "2.2", height: "6", rx: "1.1" }),
        h("circle", { cx: "12", cy: "17.2", r: "1.3" }),
      ),
    ),
  );
  const rows = [];
  for (let k = 0; k < SLOTS; k += 1) {
    rows.push(
      h("i", { class: "tk-k", ...R("tank-glyph", k) }),
      h(
        "div",
        { class: "tk-r" },
        h(
          "span",
          { class: "tk-n" },
          h("span", { class: "tk-h", ...R("tank-label-head", k) }),
          h("span", { class: "tk-t", ...R("tank-label-tail", k) }),
          h(
            "svg",
            { class: "tk-gl", "aria-hidden": "true" },
            h("use", { class: "gw", href: "#tw" }),
            h("use", { class: "gf", href: "#tf" }),
            h("use", { class: "gx", href: "#tx" }),
          ),
        ),
        h(
          "span",
          { class: "tk-b" },
          h("span", { class: "tk-f", ...R("tank-bar", k) }),
        ),
        h(
          "span",
          { class: "tk-v" },
          h("span", { class: "tk-num", ...R("tank-value", k) }),
          h("span", { class: "tk-u", ...R("tank-unit", k) }),
          h(
            "svg",
            { class: "tk-al", "aria-hidden": "true" },
            h("use", { href: "#ta" }),
          ),
        ),
      ),
    );
  }
  return {
    kind: true,
    icon: true,
    html: [
      defs,
      h("i", { class: "mo-flag", ...F("state") }),
      h("p", { class: "mo-msg", ...F("message") }),
      // Whether a fourth and a sixth tank exist: the rows' density.
      h("i", { class: "tk-q", ...R("tank-glyph", 3) }),
      h("i", { class: "tk-q", ...R("tank-glyph", 5) }),
      h("div", { class: "tk" }, rows),
    ],
    css: css(`
      .tk-defs { position: absolute; width: 0; height: 0; overflow: hidden; }
      .mo-flag[data-state=none] ~ .tk { display: none; }
      .tk { display: grid; grid-template-columns: minmax(0, auto) minmax(0, 1fr) auto; column-gap: 14px; align-content: start;
        --_p: 16px; --_m: 12px; --_n: 14px; --_v: 18px; }
      .tk-q[data-tc-slot="3"][data-state] ~ .tk { --_p: 9px; --_m: 10px; --_n: 13px; --_v: 16px; }
      .tk-q[data-tc-slot="5"][data-state] ~ .tk { --_p: 6px; --_m: 8px; --_n: 12.5px; --_v: 15px; }
      .tk-r { grid-column: 1 / -1; display: grid; grid-template-columns: subgrid; align-items: center; padding-block: var(--_p); }
      .tk-r ~ .tk-r { border-top: 0.5px solid var(--grid); }
      .tk-k, .tk-q, .tk-k:not([data-state]) + .tk-r { display: none; }
      .tk-n { display: flex; align-items: center; gap: 8px; min-width: 0; font-size: var(--_n); font-weight: 500; line-height: 1.25; }
      .tk-h { overflow-wrap: anywhere; min-width: 0; }
      .tk-t { flex: none; }
      .tk-k[data-state=fuel] + .tk-r { --_c: var(--tc-fuel); --_ci: var(--tc-fuel-icon); }
      .tk-gl { order: -1; flex: none; width: 15px; height: 15px; fill: var(--_ci); }
      .tk-gl use { display: none; }
      .tk-k[data-state=water] + .tk-r .gw, .tk-k[data-state=fuel] + .tk-r .gf,
        .tk-k[data-state=waste] + .tk-r .gx { display: inline; }
      .tk-b { position: relative; height: var(--_m); border-radius: 999px; background: var(--track); min-width: 0; }
      .tk-f { position: absolute; inset: 0 auto 0 0; border-radius: 999px; background: var(--_c);
        width: calc(var(--frac,0) * 100%); }
      .tk-f[data-zone=nosig] { display: none; }
      .tk-k[data-zone=nosig] + .tk-r .tk-b { background: none; border: 1px dashed var(--grid); }
      .tk-v { justify-self: end; display: inline-flex; align-items: center; gap: 4px; font-family: var(--tc-font-rounded);
        font-size: var(--_v); font-weight: 700; line-height: 1.1; }
      .tk-num[data-tone=warn] { color: var(--attention); }
      .tk-num[data-tone=bad] { color: var(--danger); }
      .tk-num[data-quality=missing] { color: var(--muted); }
      /* At night a warning value keeps the text colour: --attention under the night tint reads below the app's own values; its glyph keeps the tone (§9.6, TOM-665). */
      html[data-theme=night] .tk-num[data-tone=warn] { color: var(--text); }
      .tk-u { font-size: 12px; font-weight: 600; color: var(--muted); }
      .tk-al { order: -1; width: 13px; height: 13px; fill: currentColor; display: none; }
      .tk-num[data-tone=warn] ~ .tk-al { display: block; color: var(--attention); }
      .tk-num[data-tone=bad] ~ .tk-al { display: block; color: var(--danger); }
      /* The bento's cell: the rows keep their height and the block centres
         in what is left; the narrow cell's gaps give the meters room. */
      html[data-slot=tanks-budget] .tk { flex: 1 1 auto; min-height: 0; align-content: safe center; column-gap: 8px; }
    `),
  };
}
